import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from 'expo-router';
import { doc, getDoc } from 'firebase/firestore';
import nativeFirestore from '@react-native-firebase/firestore';
import { db } from '../services/firebaseConfig';
import { useThemeColors } from '../constants/theme';
import { ESCALONES, escalonDe, loQueFalta } from '../constants/niveles';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA NOTA Y EL RANGO, JUNTO A LA FOTO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Reportado por el fundador (2-oct-2026): «estaba viendo la barra de mi perfil
 * donde muestra la estrella, pero no aparece lo que te pedí: los rangos que va
 * adquiriendo el chofer según complete viajes y gane buenas recomendaciones».
 *
 * Tenía razón dos veces.
 *
 * ── 1. EL RANGO NO SE VEÍA EN NINGUNA PARTE ──────────────────────────────
 *
 * `functions/src/niveles.ts` lleva semanas calculándolo y guardándolo en
 * `users/{uid}.nivel`, pero el ÚNICO sitio de la app que lo leía era la ficha
 * del chofer que ve el PASAJERO (`map.tsx`). O sea: el chofer se ganaba un
 * rango y no podía verlo. Un sistema de rangos que el que los gana no ve no
 * motiva a nadie — es la única razón por la que existe.
 *
 * ── 2. LA ESTRELLA ERA UN DIBUJO ─────────────────────────────────────────
 *
 * El perfil pintaba cinco estrellas VACÍAS y el texto literal «0.0 (Nuevo)»,
 * escrito a mano, igual para todo el mundo. Un chofer con 4.9 de nota y
 * doscientos viajes veía «0.0 (Nuevo)». Los campos reales —`driverRatingAvg`,
 * `driverRatingCount`— los escribe `functions/src/ratings.ts` y no los leía
 * nadie.
 *
 * ── POR QUÉ LA BARRA Y NO SOLO LA MEDALLA ────────────────────────────────
 *
 * Una medalla dice dónde estás. Lo que hace que alguien siga es ver CUÁNTO
 * FALTA: «te faltan 12 viajes para Oro» es una frase que mueve a trabajar;
 * «eres Plata» no. Por eso la barra, y por eso dice las dos condiciones —
 * viajes y nota—, que es lo que el fundador pidió: «por viaje cumplido Y por
 * los buenos comentarios».
 */

type Datos = {
  nivel: string | null;
  viajes: number;
  nota: number | null;
  cuentaNotas: number;
};

export default function BarraDeNivel({
  uid,
  esChofer,
}: {
  uid: string | null | undefined;
  esChofer: boolean;
}) {
  const colores = useThemeColors();
  const [d, setD] = React.useState<Datos | null>(null);
  const [abierto, setAbierto] = React.useState(false);

  // Se relee al volver a la pantalla: después de un viaje la nota y el conteo
  // cambian, y el perfil es la primera pestaña que se mira.
  useFocusEffect(
    React.useCallback(() => {
      if (!uid) return;
      let vivo = true;
      (async () => {
        try {
          let x: any = null;
          if (Platform.OS !== 'web') {
            // SDK nativo en el móvil: con el camino de respaldo REST el SDK web
            // va anónimo y `isSelf(uid)` deniega la lectura. Mismo motivo que
            // en driver-subscription.tsx y ayuda.tsx.
            const s = await nativeFirestore().collection('users').doc(uid).get();
            x = s?.exists?.() ? s.data() : null;
          } else {
            const s = await getDoc(doc(db, 'users', uid));
            x = s.exists() ? s.data() : null;
          }
          if (!vivo || !x) return;
          const nota = esChofer ? x.driverRatingAvg : x.passengerRatingAvg;
          const cuenta = esChofer ? x.driverRatingCount : x.passengerRatingCount;
          setD({
            nivel: x.nivel ? String(x.nivel) : 'BRONCE',
            viajes: Number(x.viajesCompletados ?? x.driverTotalLifetimeTrips ?? 0) || 0,
            nota: typeof nota === 'number' ? nota : null,
            cuentaNotas: Number(cuenta ?? 0) || 0,
          });
        } catch (e: any) {
          // Sin esto la cabecera del perfil se queda como estaba. No se pinta
          // un «0.0» inventado: eso es justo lo que se vino a quitar.
          console.warn('[BarraDeNivel] no se pudo leer:', e?.message);
        }
      })();
      return () => { vivo = false; };
    }, [uid, esChofer])
  );

  if (!d) return null;

  const tieneNota = d.nota != null && d.cuentaNotas > 0;
  const estrellas = tieneNota ? Math.round(d.nota as number) : 0;

  // ── LA NOTA ───────────────────────────────────────────────────────────────
  const filaNota = (
    <View style={estilos.filaNota}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Ionicons
          key={i}
          name={tieneNota && i <= estrellas ? 'star' : 'star-outline'}
          size={14}
          color="#FFD700"
          style={{ marginRight: 2 }}
        />
      ))}
      <Text style={[estilos.textoNota, { color: colores.textSecondary }]}>
        {tieneNota
          ? `${(d.nota as number).toFixed(1)} (${d.cuentaNotas})`
          : 'Nuevo'}
      </Text>
    </View>
  );

  // Los rangos son del chofer. Un pasajero ve su nota y ya.
  if (!esChofer) return filaNota;

  const actual = escalonDe(d.nivel);
  const falta = loQueFalta(d.nivel, d.viajes, d.nota, d.cuentaNotas);

  // Cuánto del camino lleva hecho, por viajes. Es lo único que se puede dibujar
  // como barra: la nota no es un progreso, es una condición que se cumple o no.
  const desde = ESCALONES.find((e) => e.nivel === actual.nivel)?.viajes ?? 0;
  const hasta = falta?.siguiente.viajes ?? d.viajes;
  const avance = falta && hasta > desde
    ? Math.max(0, Math.min(1, (d.viajes - desde) / (hasta - desde)))
    : 1;

  return (
    <View>
      {filaNota}

      <TouchableOpacity
        onPress={() => setAbierto((v) => !v)}
        activeOpacity={0.75}
        accessibilityRole="button"
        accessibilityLabel={`Tu rango es ${actual.nivel}. Toca para ver qué falta para el siguiente.`}
        style={estilos.caja}
      >
        <View style={estilos.filaRango}>
          <View style={[estilos.medalla, { backgroundColor: actual.color + '22', borderColor: actual.color }]}>
            <Text style={estilos.emoji}>{actual.emoji}</Text>
            <Text style={[estilos.nombreRango, { color: actual.color }]}>{actual.nivel}</Text>
          </View>
          <Text style={[estilos.viajes, { color: colores.textSecondary }]}>
            {d.viajes} viaje{d.viajes === 1 ? '' : 's'}
          </Text>
          <Ionicons
            name={abierto ? 'chevron-up' : 'chevron-down'}
            size={14}
            color={colores.textSecondary}
          />
        </View>

        {falta ? (
          <>
            <View style={[estilos.riel, { backgroundColor: colores.border }]}>
              <View
                style={[
                  estilos.relleno,
                  { width: `${Math.round(avance * 100)}%`, backgroundColor: falta.siguiente.color },
                ]}
              />
            </View>
            <Text style={[estilos.pie, { color: colores.textSecondary }]}>
              {falta.viajesQueFaltan > 0
                ? `Te faltan ${falta.viajesQueFaltan} viaje${falta.viajesQueFaltan === 1 ? '' : 's'} para ${falta.siguiente.emoji} ${falta.siguiente.nivel}`
                : `Ya tienes los viajes para ${falta.siguiente.emoji} ${falta.siguiente.nivel}`}
            </Text>
          </>
        ) : (
          <Text style={[estilos.pie, { color: actual.color }]}>
            Estás en el rango más alto. {actual.queDa}
          </Text>
        )}

        {abierto && falta ? (
          <View style={[estilos.detalle, { borderTopColor: colores.border }]}>
            <Text style={[estilos.detalleTitulo, { color: colores.text }]}>
              Para llegar a {falta.siguiente.emoji} {falta.siguiente.nivel} necesitas:
            </Text>
            <Text style={[estilos.detalleLinea, { color: colores.textSecondary }]}>
              {falta.viajesQueFaltan > 0 ? '•' : '✓'} {falta.siguiente.viajes} viajes completados
              {falta.viajesQueFaltan > 0 ? ` — te faltan ${falta.viajesQueFaltan}` : ' — ya está'}
            </Text>
            <Text style={[estilos.detalleLinea, { color: colores.textSecondary }]}>
              {falta.notaQueHaceFalta ? '•' : '✓'} nota de {falta.siguiente.nota} o más
              {falta.notaQueHaceFalta
                ? ` — tienes ${tieneNota ? (d.nota as number).toFixed(1) : 'ninguna todavía'}`
                : ' — ya está'}
            </Text>
            <Text style={[estilos.detalleLinea, { color: colores.textSecondary }]}>
              {falta.notasQueFaltan > 0 ? '•' : '✓'} {falta.siguiente.minNotas} calificaciones
              {falta.notasQueFaltan > 0 ? ` — te faltan ${falta.notasQueFaltan}` : ' — ya está'}
            </Text>
            {/* Se dice qué da el rango, y se dice la verdad: hoy es
                reconocimiento. Prometer un descuento que no existe se descubre
                el primer mes. */}
            <Text style={[estilos.detalleNota, { color: colores.textSecondary }]}>
              {falta.siguiente.queDa} El rango se ve en tu ficha cuando ofertas un viaje.
            </Text>
          </View>
        ) : null}
      </TouchableOpacity>
    </View>
  );
}

const estilos = StyleSheet.create({
  filaNota: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  textoNota: { fontSize: 13, marginLeft: 4, fontWeight: 'bold' },
  caja: { marginBottom: 6 },
  filaRango: { flexDirection: 'row', alignItems: 'center', marginBottom: 5 },
  medalla: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  emoji: { fontSize: 11, marginRight: 4 },
  nombreRango: { fontSize: 11, fontWeight: '800', letterSpacing: 0.4 },
  viajes: { fontSize: 12, marginLeft: 8, flex: 1 },
  riel: { height: 5, borderRadius: 3, overflow: 'hidden' },
  relleno: { height: 5, borderRadius: 3 },
  pie: { fontSize: 11.5, marginTop: 4 },
  detalle: { marginTop: 9, paddingTop: 8, borderTopWidth: StyleSheet.hairlineWidth },
  detalleTitulo: { fontSize: 12.5, fontWeight: '700', marginBottom: 5 },
  detalleLinea: { fontSize: 12, lineHeight: 18 },
  detalleNota: { fontSize: 11.5, lineHeight: 17, marginTop: 6, fontStyle: 'italic' },
});
